import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { apiService, RedTeamDomain } from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";

const Domains: React.FC = () => {
  const [domains, setDomains] = useState<RedTeamDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"all" | "available" | "reserved">("all");
  const [filter, setFilter] = useState("");
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const domainRes = await apiService.getRedTeamDomains({ status });
    if (domainRes.success) setDomains(domainRes.data || []);
    else toast.showError("Failed", domainRes.message || "Unable to load domains");
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filtered = useMemo(
    () =>
      domains.filter((d) =>
        d.domainname.toLowerCase().includes(filter.toLowerCase()),
      ),
    [domains, filter],
  );

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3 align-items-center justify-content-between">
        <h2 className="m-0 text-xl">Domains</h2>
        <div className="flex flex-wrap gap-2">
          <Dropdown
            value={status}
            options={[
              { label: "All", value: "all" },
              { label: "Available", value: "available" },
              { label: "Reserved", value: "reserved" },
            ]}
            onChange={(e) => setStatus(e.value)}
            placeholder="Status"
          />
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter domains"
            />
          </IconField>
          <Button
            icon="pi pi-refresh"
            className="p-button-outlined"
            onClick={load}
            loading={loading}
          />
        </div>
      </div>

      <p className="text-600 text-sm mt-0 mb-3">
        Domains are reserved when you submit or modify infrastructure, or through
        phishing registration. Release happens when infrastructure is destroyed.
      </p>

      <DataTable
        value={filtered}
        loading={loading}
        paginator
        rows={20}
        emptyMessage="No domains found"
        size="small"
      >
        <Column field="domainname" header="Domain" sortable />
        <Column
          header="Status"
          body={(row: RedTeamDomain) =>
            row.reserved ? (
              <Tag severity="warning" value="Reserved" />
            ) : (
              <Tag severity="success" value="Available" />
            )
          }
        />
        <Column field="project_reference" header="Project" />
        <Column
          header="Flags"
          body={(row: RedTeamDomain) =>
            [
              row.relay ? "relay" : null,
              row.phishing ? "phishing" : null,
              row.website ? "website" : null,
              row.compromised ? "compromised" : null,
            ]
              .filter(Boolean)
              .join(", ") || "—"
          }
        />
      </DataTable>
    </div>
  );
};

export default Domains;
